import { useEffect } from 'react';

/**
 * Hook for handling keyboard shortcuts.
 *
 * @param {Object} keyMap - Map of key combos to handlers
 *   e.g., { 'Enter': handler, 'Ctrl+s': saveHandler, '1': rateBlackout }
 * @param {boolean} enabled - Whether shortcuts are active
 */
export default function useKeyboard(keyMap, enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e) => {
      // Build the key combo string
      const parts = [];
      if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
      if (e.shiftKey) parts.push('Shift');
      if (e.altKey) parts.push('Alt');

      // Normalize the key name
      let key = e.key;
      if (key === ' ') key = 'Space';
      if (key.length === 1) key = key.toLowerCase();
      parts.push(key);

      const combo = parts.join('+');

      // Check direct key match (without modifiers) for simple cases
      const simpleKey = e.key.length === 1 ? e.key : e.key;

      if (keyMap[combo]) {
        e.preventDefault();
        keyMap[combo](e);
      } else if (keyMap[simpleKey] && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // Only match simple keys if no modifiers are pressed
        // Don't preventDefault for simple keys — let the caller decide
        keyMap[simpleKey](e);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [keyMap, enabled]);
}
